/**
 * =============================================================================
 * MAP DEMO PAGE
 * =============================================================================
 * 
 * Demo page for the LGBT Resources Map component.
 * Showcases interactive map features and provides API key generation.
 * 
 * Features:
 * - Full interactive map preview
 * - Theme and Youth Safe Mode testing
 * - API key generation for embedding
 * - Embedded mode support for iframe integration
 * - Responsive design with wave backgrounds
 * 
 * Routes:
 * - /map - Full demo page
 * - /map?embedded=true - Embedded map only (for iframes)
 * - /map?hideFooter=true - Hide footer (for lgbtnearme iframe)
 * 
 * =============================================================================
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MapDemo.css';
import LGBTResourcesMap from './components/LGBTResourcesMap';
import ApiKeyGenerator from './components/ApiKeyGenerator';
import ResourcesNav from './components/ResourcesNav';
// Footer removed per Aaron's request (Jan 2026)
// import Footer from './components/Footer';
import StartPageBackground from './assests/Start_Page.png';
import WhiteWaveBackground from './assests/White_Wave.png';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * MapDemo Component
 * 
 * Demo page that showcases the LGBT Resources Map.
 * Supports both full demo mode and embedded iframe mode.
 * 
 * URL Parameters (for embedded mode):
 * - embedded: 'true' to show only the map
 * - youthSafe: 'true' to enable Youth Safe Mode
 * - theme: 'purple', 'inverted', 'monochrome', or 'ocean'
 * - apiKey: API key for authentication
 * - hideFooter: 'true' to hide the footer (for lgbtnearme)
 */
function MapDemo() {
  
  // ===========================================================================
  // URL PARAMETER PARSING
  // ===========================================================================
  
  const urlParams = new URLSearchParams(window.location.search);
  const isEmbedded = urlParams.get('embedded') === 'true';
  const theme = urlParams.get('theme') || 'purple';
  const youthSafeParam = urlParams.get('youthSafe') === 'true';
  
  // Parameter for lgbtnearme customization
  const hideFooter = urlParams.get('hideFooter') === 'true';
  
  // API key from URL or environment variable (never commit actual keys!)
  const apiKey = urlParams.get('apiKey') || process.env.REACT_APP_MAP_API_KEY;
  
  // ===========================================================================
  // HOOKS
  // ===========================================================================
  
  const navigate = useNavigate();
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  // Testing mode state (only used in demo mode, not embedded)
  const [testYouthSafe, setTestYouthSafe] = useState(youthSafeParam);
  const [testTheme, setTestTheme] = useState(theme);
  
  // ===========================================================================
  // THEME CONFIGURATION
  // ===========================================================================
  
  /**
   * Available color themes matching the ApiKeyGenerator options.
   * Each theme has an ID, display name, and gradient preview.
   */
  const colorThemes = [
    { id: 'purple', name: 'Standard', preview: 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)' },
    { id: 'inverted', name: 'Inverted', preview: 'linear-gradient(135deg, #af8bef 0%, #7DCD7D 100%)' },
    { id: 'monochrome', name: 'Monochrome', preview: 'linear-gradient(135deg, #333333 0%, #666666 100%)' },
    { id: 'ocean', name: 'Ocean Blue', preview: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' }
  ];
  
  // ===========================================================================
  // EFFECTS
  // ===========================================================================
  
  /**
   * Apply page-specific styles based on embedded mode.
   * Sets up background images and body classes.
   */
  React.useEffect(() => {
    // Add class for page-specific styling
    document.body.classList.add('map-demo-page');
    
    if (isEmbedded) {
      document.body.classList.add('embedded');
      document.documentElement.style.setProperty('--widget-theme', theme);
    } else {
      // Set CSS variables for background images
      document.documentElement.style.setProperty('--start-page-bg-image', `url(${StartPageBackground})`);
      document.documentElement.style.setProperty('--white-wave-bg-image', `url(${WhiteWaveBackground})`);
      
      // Add class to hide footer if requested
      if (hideFooter) {
        document.body.classList.add('hide-footer');
      }
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('embedded');
      document.body.classList.remove('map-demo-page');
      document.body.classList.remove('hide-footer');
      document.documentElement.style.removeProperty('--start-page-bg-image');
      document.documentElement.style.removeProperty('--white-wave-bg-image');
    };
  }, [isEmbedded, theme, hideFooter]);
  
  // ===========================================================================
  // EMBEDDED MODE RENDER
  // ===========================================================================
  
  /**
   * When embedded in an iframe, show only the map component.
   * Uses URL parameters for configuration.
   */
  if (isEmbedded) {
    return (
      <div style={{ 
        width: '100%', 
        height: '100vh', 
        background: '#491994',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <LGBTResourcesMap 
          apiKey={apiKey}
          theme={theme}
          youthSafe={youthSafeParam}
        />
      </div>
    );
  }
  
  // ===========================================================================
  // FULL DEMO PAGE RENDER
  // ===========================================================================
  
  return (
    <>
      {/* Fixed Background Container - Start Page image (now hidden via CSS) */}
      <div className="map-fixed-background-container"></div>
      
      {/* Navigation */}
      <ResourcesNav />
      
      {/* Single Unified Section - All content in one wrapper for unified scrolling */}
      <div className="map-bottom-wrapper">
        
        {/* Hero Section - Intro Text */}
        <div className="map-hero-section">
          <p className="map-hero-tagline">ADD THE LARGEST LGBTQ+ RESOURCE DATABASE TO YOUR SITE</p>
          <h1 className="map-hero-title">LGBT Near Me Website Widget</h1>
          <p className="map-hero-text">
            The <a href="https://www.lgbthotline.org" target="_blank" rel="noopener noreferrer" className="map-hero-link">LGBT National Help Center</a> is excited to invite community organizations in adding a <strong><u>free</u></strong> custom search tool that can be embedded on any website to encourage community connection and help find critically needed resources, LGBTQ+ owned businesses, social and support groups and much more!
          </p>
          <p className="map-hero-text">
            With over 21,000 resources, LGBT Near Me is the largest resource database of its kind, and is constantly being updated and maintained by skilled LGBTQ+ community members to be up to date and accurate.
          </p>
          <p className="map-hero-text">
            Designed to display either as a Resource List or a Resource Map, the LGBT Near Me Widget can be personalized to your website and to your community member's age, offering an all ages results or as Youth specific.
          </p>
        </div>
        
        {/* Feature Cards */}
        <div className="map-features-section">
          {/* How It Works Card */}
          <div className="map-feature-card">
            <h2>How It Works</h2>
            <ol className="how-it-works-list">
              <li>Request your API key below</li>
              <li>Copy the embed code</li>
              <li>Paste into your website</li>
              <li>Once approved visitors can find local resources through your website</li>
            </ol>
          </div>
          
          {/* Features Card */}
          <div className="map-feature-card">
            <h2>Features</h2>
            <ul>
              <li>Search by ZIP/Postal Code and radius</li>
              <li>Filter by search category</li>
              <li><strong><u>Free</u></strong> to use</li>
              <li>Youth-safe mode available</li>
              <li>Reliably and constantly updated by LGBT National Help Center resource staff</li>
            </ul>
          </div>
        </div>
        
        {/* Choose Between Section - Slider Container */}
        <div className="map-choose-between-section">
          <div className="map-choose-between-card">
            <h3>Users can choose Resource List or Resource Map Display</h3>
            <div className="map-resource-type-slider">
              <button 
                className="map-resource-type-btn"
                onClick={() => navigate('/widget')}
              >
                <span className="btn-label">Resource List</span>
                <span className="btn-subtitle">Floating Button</span>
              </button>
              <button 
                className="map-resource-type-btn active"
                onClick={() => {}}
              >
                <span className="btn-label">Resource Map</span>
                <span className="btn-subtitle">Static Iframe</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Testing Controls Section */}
        <div className="map-testing-controls">
          <div className="testing-slider-container">
            {/* Header */}
            <div className="testing-slider-header">
              <h3>Live Demo Mode</h3>
              <span className="testing-slider-badge">Demo Only</span>
            </div>
            <p className="testing-slider-description">
              Test different modes and colour themes before generating your API key
            </p>
            
            {/* Content Mode Toggle - Adult / Youth Safe */}
            <div className="testing-section">
              <div className="testing-section-label">Content Mode</div>
              <div className="testing-mode-slider">
                {/* Adult Mode Button */}
                <button
                  className={`testing-mode-btn ${!testYouthSafe ? 'active' : ''}`}
                  onClick={() => setTestYouthSafe(false)}
                >
                  <span className="testing-mode-icon">👥</span>
                  <span className="testing-mode-label">Adult Mode</span>
                </button>
                
                {/* Sliding Indicator */}
                <div className="testing-mode-indicator" style={{
                  transform: testYouthSafe ? 'translateX(100%)' : 'translateX(0)'
                }}></div>
                
                {/* Youth Safe Mode Button */}
                <button
                  className={`testing-mode-btn ${testYouthSafe ? 'active' : ''}`}
                  onClick={() => setTestYouthSafe(true)}
                >
                  <span className="testing-mode-icon">🛡️</span>
                  <span className="testing-mode-label">Youth Safe</span>
                </button>
              </div>
            </div>
            
            {/* Colour Theme Selector */}
            <div className="testing-section">
              <div className="testing-section-label">Colour Theme</div>
              <div className="testing-theme-grid">
                {colorThemes.map(themeOption => (
                  <button
                    key={themeOption.id}
                    className={`testing-theme-btn ${testTheme === themeOption.id ? 'active' : ''}`}
                    onClick={() => setTestTheme(themeOption.id)}
                  >
                    <div 
                      className="testing-theme-preview" 
                      style={{ background: themeOption.preview }}
                    />
                    <span className="testing-theme-name">{themeOption.name}</span>
                    {testTheme === themeOption.id && (
                      <span className="testing-theme-check">✔</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Current Selection Display */}
            <div className="testing-mode-info">
              <div className="testing-info-combined">
                <div className="testing-info-row">
                  <span className="testing-info-label">Mode:</span>
                  <span className={`testing-info-value ${testYouthSafe ? 'youth' : 'adult'}`}>
                    {testYouthSafe ? '🛡️ Youth Safe' : '👥 Adult Mode'}
                  </span>
                </div>
                <div className="testing-info-row">
                  <span className="testing-info-label">Theme:</span>
                  <span className="testing-info-value theme">
                    <span 
                      className="testing-info-dot" 
                      style={{ background: colorThemes.find(t => t.id === testTheme)?.preview }}
                    />
                    {colorThemes.find(t => t.id === testTheme)?.name}
                  </span>
                </div>
                <div className="testing-info-note testing-info-note-cta">
                  See your selections applied to the map below 👇
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* 
         * Map Component - Controlled by testing sliders above.
         * Key prop forces re-render when mode or theme changes.
         */}
        <div className="map-component-section">
          <LGBTResourcesMap 
            apiKey={apiKey}
            youthSafe={testYouthSafe}
            theme={testTheme}
            key={`${testYouthSafe ? 'youth' : 'adult'}-${testTheme}`}
          />
        </div>

        {/* API Key Generator - Ready to Integrate */}
        <div className="map-api-section">
          <ApiKeyGenerator productType="map" />
        </div>
        
        <div className="map-embed-section">
          {/* Reserved for future embed instructions */}
        </div>
      </div>

      {/* Footer removed per Aaron's request (Jan 2026) */}
    </>
  );
}

export default MapDemo;